import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Accessibility, SearchTable } from "../../component/component";
import { getProtectionPlans, triggerS3FileReadCsvProtectionPlan, uploadProtectionPlans } from "../../service/Service";
import { AppContext } from "../../store/AppProvider";
import { SHOW_ERROR_ALERT, SHOW_INFO_ALERT, SHOW_SUCCESS_ALERT, SHOW_WARNING_ALERT } from "../../store/reducer/AlertNotifierReducer";
import { Button } from "@mui/material";
import { styled } from '@mui/material/styles';
import { CloudUpload } from "@mui/icons-material";
import axios from "axios";
import { FEATURES, fileSizeRestriction } from "../../utils/Utils";
import { ErrorConstant, Warnings } from "../../utils/Constants";

export const UpdateProtectionPlan = () => {
    const { setAlert, setShowLoader } = useContext(AppContext)
    const popInputRef = useRef(null);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    const [globalFilter, setGlobalFilter] = useState('');
    const [showTable, setShowTable] = useState(false)
    const [showLoading, setShowLoading] = useState(false)
    const [refresh, setRefresh] = useState(false)
    const [tableData, setTableData] = useState([])
    const [totalRecordCount, setTotalRecordCount] = useState(0)
    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    useEffect(() => {
        const fetchProtectionPlans = async () => {
            try {
                setShowLoading(true)
                // Reset the filter once we clear the search.
                if (!globalFilter) {
                    setGlobalFilter('');
                }
                const { data, totalCount } = await getProtectionPlans(pagination, globalFilter)
                if (data?.length) {
                    setShowTable(true)
                    setTableData(data)
                    setTotalRecordCount(totalCount)
                }
            } catch (error) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
            } finally {
                setShowLoading(false)
            }
        }
        fetchProtectionPlans()
    }, [pagination, globalFilter, refresh, setAlert])

    const handleExportRegData = async (columns) => {
        try {
            setAlert({ type: SHOW_INFO_ALERT, alertMessage: "CSV Download started..." })
            const fetchedData = await getProtectionPlans({ ...pagination, ...globalFilter, isDownload: true });
            window.open(fetchedData, "_blank");
            setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "CSV Download successful!" })
        } catch (e) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.downloadingCSV })
        }
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: 'hardware_sku',
                header: 'Hardware SKU',
                size: 150,
            },
            {
                accessorKey: 'coverage_channel',
                header: 'Coverage Channel',
                size: 130,
            },
            {
                accessorKey: 'coverage_term',
                header: 'Coverage Term',
                size: 150,
            },
            {
                accessorKey: 'hardware_msrp',
                header: 'Hardware MSRP',
                size: 150,
            },
            {
                accessorKey: 'warranty_sku',
                header: 'Warranty SKU',
                size: 150,
            },
            {
                accessorKey: 'product_type',
                header: 'Product Type',
                size: 150,
            },
            {
                accessorKey: 'days_after_purchase_maximum',
                header: 'Days After Purchase Maximum',
                size: 150,
            },
            {
                accessorKey: 'price_band_min',
                header: 'Price Band Min',
                size: 150,
            },

            {
                accessorKey: 'price_band_max',
                header: 'Price Band Max',
                size: 150,
            },
            {
                accessorKey: 'suggested_retail_price',
                header: 'Suggested Retail Price',
                size: 150,
            },
            {
                accessorKey: 'total_direct_cost',
                header: 'Total Direct Cost',
                size: 150,
            },
        ],
        [],
    );

    const uploadCSV = async (event) => {
        event?.preventDefault()
        try {
            const csvFile = event.target.files[0];
            if (fileSizeRestriction(csvFile)) {
                removeProofRef()
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: Warnings.csvFileSizeExceeded })
                return;
            }
            if (csvFile.type !== 'text/csv') {
                removeProofRef()
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: Warnings.csvFileType })
                return;
            }
            setShowLoader(true)
            const payload = {
                file_type: csvFile.type,
                file_name: csvFile.name,
                file_size: csvFile.size
            }
            const data = await uploadProtectionPlans(payload)
            if (Boolean(data?.s3_url)) {
                const S3Url = data?.s3_url?.split('&x-amz-security-token')[0];
                const uploadData = await axios({ method: "PUT", url: S3Url, data: csvFile, headers: { 'Content-Type': csvFile.type } })
                if (uploadData?.statusText === 'OK') {
                    const datam = await triggerS3FileReadCsvProtectionPlan({ file_name: csvFile.name })
                    if (datam?.status === 'SUCCESS') {
                        setRefresh(!refresh)
                        setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Protection Plan Uploaded Successfully!" })
                    }
                }
            }
        } catch (error) {
            removeProofRef()
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: error?.message || ErrorConstant.error })
        } finally {
            setShowLoader(false)
        }
    }

    const removeProofRef = () => {
        popInputRef.current.value = "";
    }

    return <div>
        <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('Protection Plan').toUpperCase()}</div>
        <br />
        <Accessibility feature={FEATURES.UPLOAD_PROTECTION_PLAN_INFO}>
            <div>
                <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUpload />}
                >
                    Upload
                    <VisuallyHiddenInput type="file"
                        onChange={(event) => uploadCSV(event)}
                        accept="csv"
                        onClick={() => removeProofRef()}
                        ref={popInputRef} />
                </Button>
                &nbsp;New Protection Plan
            </div>
        </Accessibility>

        <SearchTable
            id="search_table"
            showTable={showTable}
            columns={columns}
            pagination={pagination}
            setPagination={setPagination}
            totalRecordCount={totalRecordCount}
            showTableLoading={showLoading}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            isEnableGlobalFilter={true}
            tableData={tableData}
            handleExportData={(columns) => handleExportRegData(columns)} />
    </div>
}
